import { memo, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { usePlayer } from "@/context/PlayerContext";

const hash = (s = "") => { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; };

const makeBars = (seed, n) => {
  let x = hash(seed) || 1;
  const rnd = () => { x ^= x << 13; x ^= x >>> 17; x ^= x << 5; return ((x >>> 0) % 1000) / 1000; };
  return Array.from({ length: n }, (_, i) => {
    const env = 0.45 + 0.55 * Math.sin((i / n) * Math.PI) ** 0.6;
    return Math.max(0.12, Math.min(1, env * (0.35 + rnd() * 0.75)));
  });
};

const rgb = (h) => (h > 0.78 ? "bg-white" : h > 0.5 ? "bg-amber-500" : "bg-sky-500");
const COLORS = {
  lcd: { played: () => "bg-amber-400", idle: () => "bg-sky-300/35" },
  rgb: { played: rgb, idle: (h) => `${rgb(h)} opacity-35` },
  default: { played: () => "bg-emerald-400", idle: () => "bg-zinc-400/40" },
};

// Bars are rendered once per layer; progress/hover only change a clip-path, so playback doesn't re-layout hundreds of nodes.
const Bars = memo(({ heights, cls, anim }) => (
  <div className="absolute inset-0 flex items-center gap-px sm:gap-[2px]">
    {heights.map((h, i) => <span key={i} className={`${anim ? "wave-bar " : ""}flex-1 rounded-full ${cls(h)}`} style={{ height: `${h * 100}%`, animationDelay: anim ? `${i * 6}ms` : undefined }} />)}
  </div>
));

export const Waveform = ({ seed, progress = 0, onSeek, bars = 110, height = "h-16", active = false, testid = "waveform", variant = "default" }) => {
  const ref = useRef();
  const [hover, setHover] = useState(null);
  const [drag, setDrag] = useState(false);
  const heights = useMemo(() => makeBars(seed, bars), [seed, bars]);
  const ratioAt = (e) => { const r = ref.current.getBoundingClientRect(); return Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1); };
  const c = COLORS[variant] || COLORS.default;
  const clip = (r) => ({ clipPath: `inset(0 ${(1 - r) * 100}% 0 0)` });

  return (
    <div ref={ref} data-testid={testid} role="slider" aria-label="Posizione nel brano" aria-valuenow={Math.round(progress * 100)} aria-valuemin={0} aria-valuemax={100} tabIndex={0}
      className={`relative ${height} overflow-hidden cursor-pointer select-none touch-none`}
      onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); setDrag(true); onSeek?.(ratioAt(e)); }}
      onPointerMove={(e) => { setHover(ratioAt(e)); if (drag) onSeek?.(ratioAt(e)); }}
      onPointerUp={() => setDrag(false)}
      onPointerLeave={() => { setHover(null); setDrag(false); }}
      onKeyDown={(e) => { if (e.key === "ArrowRight") onSeek?.(Math.min(progress + 0.05, 1)); if (e.key === "ArrowLeft") onSeek?.(Math.max(progress - 0.05, 0)); }}>
      <Bars heights={heights} cls={c.idle} anim />
      {hover != null && hover > progress && <div className="absolute inset-0 opacity-50" style={clip(hover)}><Bars heights={heights} cls={c.played} /></div>}
      <div className={`absolute inset-0 ${active ? "wave-glow" : ""}`} style={clip(progress)}><Bars heights={heights} cls={c.played} /></div>
      {progress > 0 && <span className="pointer-events-none absolute top-0 bottom-0 w-[2px] bg-white shadow-[0_0_12px_#fff]" style={{ left: `${progress * 100}%` }} />}
    </div>
  );
};

export const useTrackWave = (release, idx) => {
  const { current, time, duration, seek, playRelease, playing } = usePlayer();
  const track = release?.tracks?.[idx];
  const isCur = !!(current && track && current.id === track.id);
  const progress = isCur && duration ? time / duration : 0;
  const onSeek = (r) => {
    if (!track) return;
    if (isCur && duration) return seek(r * duration);
    if (!playRelease(release, idx, r)) toast("Nessun audio disponibile per questo brano");
  };
  return { progress, onSeek, isCur, active: isCur && playing, track };
};
